import { Schema, type } from "@colyseus/schema";

/**
 * Per-cube survival state (warmth + hunger + stamina meters + threshold-derived factors).
 * Lives on Cube as `cube.survival` to dodge the @colyseus/schema 64-field cap.
 *
 * Spec: docs/superpowers/specs/2026-04-30-survival-design.md §4
 * KTS source: MapScript.galaxy:4080-4319 (gf_PlayerStatusUpdate + gf_ModifyKoboldHealthStatus)
 *
 * Meters are TRANSIENT — written by survival tick at 0.5s cadence.
 * PenaltyFactor fields are DERIVED — written by threshold state machine after meter update;
 * read by derived-stats.computeLifeRegen (hungerRegen) and stamina-factor.ts (warmthStamina).
 */
export class SurvivalBag extends Schema {
  /** TRANSIENT meter, 0..100. */
  @type("uint8")  warmth: number = 100;
  /** TRANSIENT meter, 0..100. */
  @type("uint8")  hunger: number = 100;
  /** TRANSIENT meter, 0..100. */
  @type("uint8")  stamina: number = 100;

  /**
   * DERIVED — basis points (10000 = 1.0 = no penalty).
   * Read by derived-stats.computeLifeRegen as `lifeRegenMultiplier -= (1 - factorBp/10000)`.
   * Bucket map: hunger=0 → 0, 1-25 → 2500, 25-50 → 5000, 50-75 → 7500, 75-100 → 10000.
   */
  @type("uint16") hungerRegenPenaltyFactorBp: number = 10000;

  /**
   * DERIVED — basis points (10000 = 1.0 = no penalty, multiplier on stamina drain).
   * Read by stamina-factor.ts.
   * Bucket map: warmth=0 → 10000, 1-25 → 11200, 25-50 → 10800, 50-75 → 10400, 75-100 → 10000.
   */
  @type("uint16") warmthStaminaPenaltyFactorBp: number = 10000;

  /**
   * V-key walk toggle. When true, MOVEMENT_MULTIPLIERS["WalkToggled"] (× 0.6)
   * applies. Synced to client so the run/walk state icon can render.
   *
   * Relocated from Cube to SurvivalBag (2026-05-12) — moving main's KoboldMeta
   * sub-Schema onto Cube (commit 001b66d) plus this branch's Spellbook
   * sub-Schema independently each pushed Cube +1 field. Stacked, the two
   * additions overflow @colyseus/schema's 64-field cap, parking `meta` at
   * index 64 (every ADD on `meta` corrupts the wire — clients see refId-not-
   * found floods and cube.x/z arrive as NaN). Moving this boolean off Cube
   * drops it back to 64 fields with `meta` at index 63. Semantically natural
   * home: it's a survival-style transient about player pace, sitting next to
   * stamina.
   */
  @type("boolean") walkToggled: boolean = false;
}
